"use client";

import { useCallback, useState } from "react";
import type { AwardShowPosterPreview } from "../types";

export interface UseAwardShowPosterPreviewResult {
  candidates: AwardShowPosterPreview[] | null;
  loading: boolean;
  error: string | null;
  fetchPreview: (name: string, year: number) => Promise<void>;
  reset: () => void;
}

export function useAwardShowPosterPreview(
  fetchPreviewFn: (
    name: string,
    year: number,
  ) => Promise<{ candidates: AwardShowPosterPreview[] }>,
): UseAwardShowPosterPreviewResult {
  const [candidates, setCandidates] = useState<
    AwardShowPosterPreview[] | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setCandidates(null);
    setError(null);
  }, []);

  const fetchPreview = useCallback(
    async (name: string, year: number) => {
      setLoading(true);
      setError(null);
      setCandidates(null);
      try {
        const result = await fetchPreviewFn(name, year);
        setCandidates(result.candidates);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to find show posters",
        );
      } finally {
        setLoading(false);
      }
    },
    [fetchPreviewFn],
  );

  return { candidates, loading, error, fetchPreview, reset };
}
